'use client'

import { useCallback, useMemo, useState } from 'react'
import { ActivityTable } from '../components/ActivityTable'
import { ActivityStats, ProcessedActivity, getActionDisplayName } from '../logic/useActivity'
import { SummaryTiles } from '../components/summary-tiles/SummaryTiles'
import {
  ActivityFilterBar,
  type ActivityMarketFilter,
  activityMatchesMarketFilter,
} from '../components/ActivityFilterBar'
import { useEulerIndexerELends } from '@/domain/euler/elends/elendsQuery'
import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { normalizeForSearch } from '@/utils/strings'

interface ActivityViewProps {
  activities: ProcessedActivity[]
  activityStats: ActivityStats
}

function activityMatchesSearch(activity: ProcessedActivity, query: string): boolean {
  if (!query) return true
  const q = normalizeForSearch(query)
  return (
    normalizeForSearch(getActionDisplayName(activity.action)).includes(q) ||
    normalizeForSearch(activity.type).includes(q) ||
    (activity.assetToken ? normalizeForSearch(activity.assetToken.symbol).includes(q) : false) ||
    (activity.vaultName ? normalizeForSearch(activity.vaultName).includes(q) : false) ||
    (activity.product ? normalizeForSearch(activity.product).includes(q) : false)
  )
}

export default function ActivityView({ activities, activityStats }: ActivityViewProps) {
  const [selectedMarkets, setSelectedMarkets] = useState<ActivityMarketFilter[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const { data: indexerVaults } = useEulerIndexerELends(E_LEND_CHAIN_ID)

  const handleToggleMarket = useCallback((market: ActivityMarketFilter) => {
    setSelectedMarkets((prev) =>
      prev.includes(market) ? prev.filter((m) => m !== market) : [...prev, market],
    )
  }, [])

  const handleClearFilters = useCallback(() => {
    setSelectedMarkets([])
  }, [])

  const filteredActivities = useMemo(
    () =>
      activities.filter(
        (a) => activityMatchesMarketFilter(a, selectedMarkets, indexerVaults) && activityMatchesSearch(a, searchQuery),
      ),
    [activities, selectedMarkets, searchQuery, indexerVaults],
  )

  return (
    <>
      <SummaryTiles activityStats={activityStats} />
      <ActivityFilterBar
        selectedMarkets={selectedMarkets}
        onToggleMarket={handleToggleMarket}
        onClearFilters={handleClearFilters}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />
      <ActivityTable activities={filteredActivities} />
    </>
  )
}
